import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const styles = {
  page: {
    backgroundColor: '#1e1e1e',
    color: '#f1f1f1',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: 'system-ui, sans-serif'
  },
  card: {
    backgroundColor: '#2a2a2a',
    border: '1px solid #333',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    padding: '16px',
    marginBottom: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  stat: {
    display: 'inline-block',
    backgroundColor: '#333',
    borderRadius: '12px',
    padding: '10px 16px',
    marginLeft: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  statActive: {
    backgroundColor: '#00b8b8'
  },
  button: {
    backgroundColor: '#00b8b8',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontWeight: 500
  },
  dangerButton: {
    backgroundColor: '#c0392b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    cursor: 'pointer'
  },
  input: {
    backgroundColor: '#333',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: '8px',
    padding: '8px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    borderBottom: '1px solid #444',
    padding: '8px',
    color: '#ccc'
  },
  td: {
    borderBottom: '1px solid #333',
    padding: '8px'
  },
  pill: {
    display: 'inline-block',
    backgroundColor: '#444',
    borderRadius: '9999px',
    padding: '2px 8px',
    fontSize: '12px',
    color: '#ccc'
  }
};

const Pill = ({ children }) => <span style={styles.pill}>{children}</span>;

export default function AdminPage() {
  const [stats, setStats] = useState({ online: 0, last7days: 0 });
  const [reported, setReported] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [size] = useState(25);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [boardPreview, setBoardPreview] = useState(null);
  const [sCols, setCols] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  let rows;
  let cols;

  const pages = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size]);

  useEffect(() => {
    fetchStats();
    fetchReported();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [page, size, filter]);

  async function getConfig(bp) {
    const cfg = JSON.parse(bp.board?.grid_config || '{}');
    setCols(cfg.columns || 8);
  }

  async function fetchStats() {
    const { data } = await api.get('/admindash.php', { params: { action: 'stats' } });
    setStats(data);
  }

  async function fetchReported() {
    const { data } = await api.get('/admindash.php', { params: { action: 'reported_community_boards' } });
    setReported(data.items || []);
  }

  async function fetchUsers() {
    setLoading(true);
    try {
      const params = { action: 'list_users_with_boards', page, size };
      if (filter) params.filter = filter;
      const { data } = await api.get('/admindash.php', { params });
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }

  async function searchUsers(e) {
    e?.preventDefault();
    if (!query.trim()) return setSearchResults([]);
    const { data } = await api.get('/admindash.php', { params: { action: 'search_users', q: query.trim() } });
    setSearchResults(data.items || []);
  }

  async function openUser(uid) {
    const { data } = await api.get('/admindash.php', { params: { action: 'get_user', user_id: uid } });
    setActiveUser(data);
  }

  async function updateUser(fields) {
    if (!activeUser?.user?.id) return;
    await api.put('/admindash.php?action=update_user', { user_id: activeUser.user.id, ...fields });
    await openUser(activeUser.user.id);
  }

  async function viewBoard(board_id) {
    const { data } = await api.get('/admindash.php', { params: { action: 'view_board', board_id } });
    setBoardPreview(data);
    getConfig(data);
  }

  async function deleteCommunitySubmission(id) {
    if (!window.confirm('Delete this community submission?')) return;
    await api.post('/admindash.php?action=delete_community_submission', new URLSearchParams({ community_board_id: id }));
    fetchReported();
  }

  const toggleFilter = (f) => {
    setFilter(prev => (prev === f ? '' : f));
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#00b8b8' }}>Admin Dashboard</h1>
        <div>
          <div
            style={{ ...styles.stat, ...(filter === 'online' ? styles.statActive : {}) }}
            onClick={() => toggleFilter('online')}
          >
            <div style={{ fontSize: '12px', color: '#aaa' }}>Online now</div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>{stats.online}</div>
          </div>
          <div
            style={{ ...styles.stat, ...(filter === 'last7days' ? styles.statActive : {}) }}
            onClick={() => toggleFilter('last7days')}
          >
            <div style={{ fontSize: '12px', color: '#aaa' }}>Logged in (7 days)</div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>{stats.last7days}</div>
          </div>
        </div>
      </div>

      {/* SEARCH */}
        <div style={styles.card}>
        <form
            onSubmit={searchUsers}
            style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}
        >
            <input
            style={{ ...styles.input, flex: 1, minWidth: '240px' }}
            placeholder="Search by id, name, or email"
            value={query}
            onChange={e => setQuery(e.target.value)}
            />
            <button type="submit" style={styles.button}>Search</button>
        </form>

        {searchResults.length > 0 && (
            <table style={styles.table}>
            <thead>
                <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Last login</th>
                <th style={styles.th}>Active</th>
                <th></th>
                </tr>
            </thead>
            <tbody>
                {searchResults.map(u => (
                <tr key={u.id}>
                    <td style={styles.td}>{u.id}</td>
                    <td style={styles.td}>{u.name}</td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}><span style={styles.pill}>{u.user_type || u.acc_type || 'individual'}</span></td>
                    <td style={styles.td}>{u.last_login_at || '-'}</td>
                    <td style={styles.td}>{u.last_active_at || '-'}</td>
                    <td style={styles.td}>
                    <button
                        onClick={() => openUser(u.id)}
                        style={{ ...styles.button, padding: '4px 10px' }}
                    >
                        Open
                    </button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        )}
        </div>


      {/* Users List */}
      <div style={styles.card}>
        <h2 style={{ color: '#00b8b8', marginBottom: '8px' }}>
          Users {filter && <span style={{ color: '#ccc' }}>({filter})</span>}
        </h2>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading…</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Boards</th>
                <th style={styles.th}>Last login</th>
                <th style={styles.th}>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={styles.td}>{u.id}</td>
                  <td style={styles.td}>{u.name}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}><Pill>{u.acc_type}</Pill></td>
                  <td style={styles.td}>
                    {(u.boards || []).map(b => (
                      <button
                        key={b.id}
                        style={{ ...styles.button, padding: '2px 8px', marginRight: '4px' }}
                        onClick={() => viewBoard(b.id)}
                      >
                        {b.name}
                      </button>
                    ))}
                  </td>
                  <td style={styles.td}>{u.last_login_at || '-'}</td>
                  <td style={styles.td}>{u.last_active_at || '-'}</td>
                  <td style={styles.td}>
                    <button style={{ ...styles.button, padding: '4px 10px' }} onClick={() => openUser(u.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Active User Modal */}
      {activeUser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={() => setActiveUser(null)}
        >
          <div
            style={{
              background: '#2a2a2a',
              borderRadius: '12px',
              padding: '20px',
              width: '90%',
              maxWidth: '700px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ color: '#00b8b8', marginBottom: '12px' }}>
              User #{activeUser.user.id}: {activeUser.user.name}
            </h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label>
                Name:
                <input
                  style={{ ...styles.input, width: '100%' }}
                  defaultValue={activeUser.user.name}
                  onBlur={e => updateUser({ name: e.target.value })}
                />
              </label>
              <label>
                Email:
                <input
                  style={{ ...styles.input, width: '100%' }}
                  defaultValue={activeUser.user.email}
                  onBlur={e => updateUser({ email: e.target.value })}
                />
              </label>
              <label>
                Type:
                <select
                  style={styles.input}
                  defaultValue={activeUser.user.user_type}
                  onChange={e => updateUser({ user_type: e.target.value })}
                >
                  <option value="individual">individual</option>
                  <option value="organisation">organisation</option>
                  <option value="unlimited">unlimited</option>
                  <option value="admin">admin</option>
                </select>
              </label>
              <div>
                <button style={styles.button}>Send Reset</button>
                <button style={{ ...styles.dangerButton, marginLeft: '8px' }}>Delete User</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Board Preview Modal */}
      {boardPreview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={() => setBoardPreview(null)}
        >
          <div
            style={{
              background: '#2a2a2a',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '800px',
              width: '100%'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '12px' }}>
              Board: <span style={{ color: '#00b8b8' }}>{boardPreview.board?.name}</span>
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${sCols}, minmax(80px, 1fr))`,
                gap: '6px'
              }}
            >
              {(boardPreview.tiles || []).map(t => (
                <div
                  key={t.id}
                  style={{
                    color: '#0c0c0cff',
                    border: '1px solid #eb8080ff',
                    borderRadius: '8px',
                    background: '#e2e2e2ff',
                    height: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                {t.image_url && (
                    <img
                        src={t.image_url}
                        alt={t.label}
                        style={{
                        width: '50%',
                        height: '50%',
                        objectFit: 'contain',
                        marginBottom: '4px',
                        pointerEvents: 'none',
                        }}
                        draggable={false}
                    />
                )}
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    textAlign: 'center',
                    userSelect: 'none',
                  }}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
